import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import CloseIcon from "@mui/icons-material/Close"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import ErrorIcon from "@mui/icons-material/Error"
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material"
import { fmtTry, fmtUsd } from "@utils/currency"
import { useMemo, useState } from "react"
import DiffViewer from "react-diff-viewer-continued"
import type { CaseResult, PlaygroundRun } from "./types"
import { useTryRate } from "./useTryRate"

interface ResultsTabProps {
  runs: PlaygroundRun[]
  selectedRunId: string | null
  onSelectRun: (runId: string) => void
  onDeleteRun: (runId: string) => void
  onClearRuns: () => void
}

function SummaryTile({
  label,
  value,
  color
}: {
  label: string
  value: string
  color?: string
}) {
  return (
    <Paper variant="outlined" sx={{ px: 1.25, py: 0.75, minWidth: 96 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700} color={color}>
        {value}
      </Typography>
    </Paper>
  )
}

function statusChip(result?: CaseResult) {
  if (!result) return <Chip size="small" variant="outlined" label="yok" />
  if (result.skipped)
    return <Chip size="small" variant="outlined" label="skip" />
  if (result.attempts.some((a) => !a.ok)) {
    return <Chip size="small" color="warning" label="hata" />
  }
  return (
    <Chip
      size="small"
      color={result.passed ? "success" : "error"}
      label={
        result.attempts.length > 1
          ? `${result.passCount}/${result.attempts.length}`
          : result.passed
            ? "PASS"
            : "FAIL"
      }
    />
  )
}

/** Total model cost of a case across all repeats (judge calls excluded). */
function caseCost(result?: CaseResult): number {
  if (!result?.attempts.length) return 0
  return result.attempts.reduce((sum, a) => sum + (a.usage?.amount ?? 0), 0)
}

function avgLatency(result?: CaseResult): number {
  if (!result?.attempts.length) return 0
  return Math.round(
    result.attempts.reduce((sum, a) => sum + a.latencyMs, 0) /
      result.attempts.length
  )
}

export default function ResultsTab(props: ResultsTabProps) {
  const { runs, selectedRunId, onSelectRun, onDeleteRun, onClearRuns } = props
  const tryRate = useTryRate()
  const [baselineId, setBaselineId] = useState<string>("")
  const [detail, setDetail] = useState<{
    current: CaseResult
    baseline?: CaseResult
  } | null>(null)

  const run = useMemo(
    () => runs.find((r) => r.id === selectedRunId) ?? runs[0],
    [runs, selectedRunId]
  )
  const baseline = useMemo(
    () => runs.find((r) => r.id === baselineId),
    [runs, baselineId]
  )

  const baselineByCase = useMemo(() => {
    const map = new Map<string, CaseResult>()
    for (const result of baseline?.results ?? []) {
      map.set(result.caseId, result)
    }
    return map
  }, [baseline])

  const delta = useMemo(() => {
    if (!run || !baseline) return null
    let improved = 0
    let regressed = 0
    for (const result of run.results) {
      const before = baselineByCase.get(result.caseId)
      if (!before || before.skipped || result.skipped) continue
      if (result.passed && !before.passed) improved += 1
      if (!result.passed && before.passed) regressed += 1
    }
    return { improved, regressed }
  }, [run, baseline, baselineByCase])

  if (!runs.length) {
    return (
      <Alert severity="info" variant="outlined">
        Henüz otonom test çalıştırılmadı. “Hazır Sorular” sekmesinden test
        setini çalıştır.
      </Alert>
    )
  }

  return (
    <Stack direction="row" spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
      <Stack sx={{ width: 250, minWidth: 250, overflow: "auto" }} spacing={0.5}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            KOŞULAR ({runs.length})
          </Typography>
          <Button size="small" color="error" onClick={onClearRuns}>
            Temizle
          </Button>
        </Stack>
        {runs.map((item) => (
          <ListItemButton
            key={item.id}
            selected={item.id === run?.id}
            onClick={() => onSelectRun(item.id)}
            sx={{ borderRadius: 1, alignItems: "flex-start" }}
          >
            <Stack flex={1} minWidth={0}>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Chip
                  size="small"
                  color={item.summary.passRate === 100 ? "success" : "default"}
                  label={`%${item.summary.passRate}`}
                />
                <Typography variant="caption" noWrap>
                  {item.snapshot.model}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" noWrap>
                {new Date(item.finishedAt).toLocaleString("tr-TR")} · #
                {item.snapshot.promptHash}
              </Typography>
            </Stack>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation()
                onDeleteRun(item.id)
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </ListItemButton>
        ))}
      </Stack>

      <Divider orientation="vertical" flexItem />

      <Stack spacing={1} sx={{ flex: 1, minWidth: 0, overflow: "auto" }}>
        {run && (
          <>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <SummaryTile
                label="Başarı"
                value={`%${run.summary.passRate}`}
                color={
                  run.summary.passRate === 100 ? "success.main" : "error.main"
                }
              />
              <SummaryTile
                label="Geçen / Toplam"
                value={`${run.summary.passed}/${run.summary.total}`}
              />
              <SummaryTile
                label="Ort. süre"
                value={`${run.summary.avgLatencyMs} ms`}
              />
              <SummaryTile
                label="Token"
                value={run.summary.totalTokens.toLocaleString()}
              />
              <SummaryTile
                label="Maliyet"
                value={`${fmtUsd(run.summary.totalCost)}${fmtTry(
                  run.summary.totalCost,
                  tryRate
                )}`}
              />
              <SummaryTile label="Model" value={run.snapshot.model} />
              <SummaryTile
                label="Prompt"
                value={`#${run.snapshot.promptHash}`}
              />
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Karşılaştır:
              </Typography>
              <Select
                size="small"
                displayEmpty
                value={baselineId}
                sx={{ minWidth: 280 }}
                onChange={(event) => setBaselineId(event.target.value)}
              >
                <MenuItem value="">— referans koşu seç —</MenuItem>
                {runs
                  .filter((item) => item.id !== run.id)
                  .map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      %{item.summary.passRate} · {item.snapshot.model} · #
                      {item.snapshot.promptHash} ·{" "}
                      {new Date(item.finishedAt).toLocaleString("tr-TR")}
                    </MenuItem>
                  ))}
              </Select>
              {delta && (
                <>
                  <Chip
                    size="small"
                    color="success"
                    icon={<CheckCircleIcon />}
                    label={`${delta.improved} iyileşti`}
                  />
                  <Chip
                    size="small"
                    color={delta.regressed ? "error" : "default"}
                    icon={<ErrorIcon />}
                    label={`${delta.regressed} bozuldu`}
                  />
                  <Typography variant="caption" color="text.secondary">
                    %{baseline?.summary.passRate} → %{run.summary.passRate}
                  </Typography>
                </>
              )}
            </Stack>

            <TableContainer sx={{ flex: 1, minHeight: 0 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Soru</TableCell>
                    <TableCell width={110}>Sonuç</TableCell>
                    {baseline && <TableCell width={110}>Referans</TableCell>}
                    <TableCell width={110}>Süre</TableCell>
                    <TableCell width={150}>
                      <Tooltip title="Bu sorunun tüm tekrarlarının toplam maliyeti (judge maliyeti hariç)">
                        <span>Maliyet</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell width={90} align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {run.results.map((result) => {
                    const before = baselineByCase.get(result.caseId)
                    const latencyDelta = before
                      ? avgLatency(result) - avgLatency(before)
                      : 0
                    const costDelta = before
                      ? caseCost(result) - caseCost(before)
                      : 0
                    return (
                      <TableRow key={result.caseId} hover>
                        <TableCell sx={{ maxWidth: 380 }}>
                          <Typography variant="body2">{result.name}</Typography>
                          {result.query && (
                            <Tooltip title={result.query}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap"
                                }}
                              >
                                “{result.query}”
                              </Typography>
                            </Tooltip>
                          )}
                          {result.attempts[0]?.error && (
                            <Typography variant="caption" color="error.main">
                              {result.attempts[0].error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{statusChip(result)}</TableCell>
                        {baseline && (
                          <TableCell>{statusChip(before)}</TableCell>
                        )}
                        <TableCell>
                          <Typography variant="caption">
                            {avgLatency(result)} ms
                            {before && latencyDelta !== 0 && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 0.5,
                                  color:
                                    latencyDelta > 0
                                      ? "error.main"
                                      : "success.main"
                                }}
                              >
                                {latencyDelta > 0 ? "+" : ""}
                                {latencyDelta}
                              </Box>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" whiteSpace="nowrap">
                            {fmtUsd(caseCost(result), 6)}
                          </Typography>
                          {tryRate > 0 && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                              whiteSpace="nowrap"
                            >
                              {fmtTry(caseCost(result), tryRate).trim()}
                            </Typography>
                          )}
                          {before && costDelta !== 0 && (
                            <Typography
                              variant="caption"
                              display="block"
                              color={
                                costDelta > 0 ? "error.main" : "success.main"
                              }
                            >
                              {costDelta > 0 ? "+" : ""}
                              {fmtUsd(costDelta, 6)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Detay / çıktı farkı">
                            <Button
                              size="small"
                              onClick={() =>
                                setDetail({ current: result, baseline: before })
                              }
                            >
                              Detay
                            </Button>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Stack>

      <Dialog
        open={Boolean(detail)}
        onClose={() => setDetail(null)}
        maxWidth="xl"
        fullWidth
        // The drawer underneath is also a modal; handle Escape here so it never
        // gets swallowed and closes the whole playground instead.
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.stopPropagation()
            setDetail(null)
          }
        }}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {detail?.current.name}
          <IconButton
            size="small"
            onClick={() => setDetail(null)}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {detail && (
            <Stack spacing={1.5}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="text.secondary"
                >
                  SORU (query)
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: "pre-wrap", mb: 1 }}
                >
                  {detail.current.query || "—"}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Maliyet: ${fmtUsd(caseCost(detail.current), 6)}${fmtTry(
                      caseCost(detail.current),
                      tryRate
                    )}`}
                  />
                  {Object.entries(detail.current.conditions ?? {}).map(
                    ([name, enabled]) => (
                      <Chip
                        key={name}
                        size="small"
                        color={enabled ? "info" : "default"}
                        variant={enabled ? "filled" : "outlined"}
                        label={`if_${name}: ${enabled ? "açık" : "kapalı"}`}
                      />
                    )
                  )}
                  {Object.entries(detail.current.variables ?? {}).map(
                    ([name, value]) => (
                      <Tooltip key={name} title={value}>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`{{${name}}}`}
                        />
                      </Tooltip>
                    )
                  )}
                </Stack>
              </Paper>
              {detail.current.attempts.map((attempt, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Paper key={index} variant="outlined" sx={{ p: 1.25 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    mb={0.75}
                  >
                    <Chip
                      size="small"
                      color={attempt.passed ? "success" : "error"}
                      label={`Deneme ${index + 1} — ${attempt.passed ? "PASS" : "FAIL"}`}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {attempt.latencyMs} ms · {attempt.usage.inputToken}→
                      {attempt.usage.outputToken} token ·{" "}
                      {fmtUsd(attempt.usage.amount, 6)}
                      {fmtTry(attempt.usage.amount, tryRate)}
                    </Typography>
                  </Stack>

                  {attempt.error && (
                    <Alert severity="error">{attempt.error}</Alert>
                  )}

                  <Stack spacing={0.25} mb={1}>
                    {attempt.assertions.map((assertion) => (
                      <Stack
                        key={`${assertion.label}-${assertion.detail ?? ""}`}
                        direction="row"
                        spacing={0.75}
                        alignItems="center"
                      >
                        <Chip
                          size="small"
                          color={assertion.passed ? "success" : "error"}
                          label={assertion.passed ? "✓" : "✕"}
                          sx={{ minWidth: 34 }}
                        />
                        <Typography variant="caption">
                          {assertion.label}
                        </Typography>
                        {assertion.detail && (
                          <Typography variant="caption" color="text.secondary">
                            — {assertion.detail}
                          </Typography>
                        )}
                      </Stack>
                    ))}
                  </Stack>

                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                      fontSize: 12,
                      maxHeight: 240,
                      overflow: "auto",
                      bgcolor: "action.hover",
                      p: 1,
                      borderRadius: 1
                    }}
                  >
                    {attempt.output || "—"}
                  </Box>
                </Paper>
              ))}

              {detail.baseline?.attempts[0] && (
                <>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    REFERANS ÇIKTI FARKI
                  </Typography>
                  <Box sx={{ fontSize: 12, maxHeight: 420, overflow: "auto" }}>
                    <DiffViewer
                      oldValue={detail.baseline.attempts[0].output}
                      newValue={detail.current.attempts[0]?.output ?? ""}
                      splitView
                      leftTitle="Referans"
                      rightTitle="Bu koşu"
                    />
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Stack>
  )
}
